import { useState, useEffect, useCallback, useRef } from "react";
import { Printer, ChefHat, RefreshCw, ShoppingBag, Bike, Loader2, Clock, Search, Bell, Volume2, VolumeX, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { sendToStarPrinter, hasPrinter } from "@/lib/starprint";

// --- New-order sound (Web Audio; no asset needed, unlocked on first tap) ---
let _audioCtx = null;
function getCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_audioCtx) _audioCtx = new AC();
  return _audioCtx;
}
function unlockAudio() {
  const ctx = getCtx();
  if (ctx && ctx.state === "suspended") ctx.resume();
}
function playChime() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume();
  const now = ctx.currentTime;
  [659.25, 830.61, 987.77].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = freq;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const t = now + i * 0.15;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    osc.start(t);
    osc.stop(t + 0.45);
  });
}

const money = (n) => `$${Number(n).toFixed(2).replace(/\.00$/, "")}`;
const FLOW = { new: "preparing", preparing: "ready", ready: "done" };
const NEXT_LABEL = { new: "Send to kitchen", preparing: "Mark ready", ready: "Collected & charged" };
const STATUS_LABEL = { new: "IN QUEUE", preparing: "IN KITCHEN", ready: "READY", done: "COLLECTED" };
const BADGE = {
  new: "bg-secondary text-secondary-foreground",
  preparing: "bg-primary text-foreground",
  ready: "bg-foreground text-background",
  done: "bg-muted text-foreground/50",
};

function ticketHtml(o) {
  const lines = (o.cart || []).map((c) => `${c.qty} x ${c.name}  ${money(c.price * c.qty)}`).join("<br>");
  return `<html><head><title>Ticket #${o.order_number}</title><style>
    @page { margin: 6mm; }
    body { font-family: 'Courier New', monospace; width: 280px; color: #000; }
    h1 { font-size: 18px; margin: 0 0 4px; }
    .big { font-size: 24px; font-weight: 700; }
    hr { border: none; border-top: 1px dashed #000; margin: 8px 0; }
    .row { font-size: 14px; line-height: 1.5; }
    .muted { font-size: 12px; }
  </style></head><body onload="window.print();">
    <h1>HARBOUR HAZE — TAKEAWAY</h1>
    <div class="big">#${o.order_number}</div>
    <div class="muted">Ordered ${new Date(o.created_at).toLocaleString()}</div>
    <hr>
    <div class="big">${o.fulfilment === "delivery" ? "DELIVERY" : "PICK UP"}</div>
    <div class="row"><b>Wanted: ${o.requested_time || "ASAP"}</b></div>
    <hr>
    <div class="row">${lines}</div>
    <hr>
    <div class="row">Subtotal: ${money(o.subtotal)}</div>
    ${o.delivery_fee ? `<div class="row">Delivery: ${money(o.delivery_fee)}</div>` : ""}
    <div class="row"><b>TOTAL: ${money(o.total)}</b> — ${o.payment_status === "paid" ? "PAID ONLINE" : "PAY ON COLLECTION"}</div>
    <hr>
    <div class="row">${o.name} · ${o.phone}</div>
    ${o.address ? `<div class="row">${o.address}</div>` : ""}
    ${o.notes ? `<div class="row">NOTES: ${o.notes}</div>` : ""}
  </body></html>`;
}

function browserPrint(o) {
  const w = window.open("", "_blank", "width=360,height=640");
  if (!w) { toast.error("Please allow pop-ups to print tickets."); return; }
  w.document.write(ticketHtml(o));
  w.document.close();
}

// Prints to the Star LAN printer when configured (and reachable via the
// Star WebPRNT Browser app); otherwise falls back to the browser print dialog.
async function printTicket(o, printerUrl, { silent = false } = {}) {
  if (hasPrinter(printerUrl)) {
    try {
      await sendToStarPrinter(printerUrl, o);
      if (!silent) toast.success(`Ticket #${o.order_number} sent to kitchen printer`);
      return;
    } catch (err) {
      toast.error(`${err.message || "Printer error"} — opening print dialog`);
    }
  }
  browserPrint(o);
}

export default function KitchenTickets() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [printerUrl, setPrinterUrl] = useState("");
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("hh_kitchen_sound") !== "off");
  const [alerts, setAlerts] = useState([]);
  const [flash, setFlash] = useState(false);
  const printerRef = useRef("");
  const soundRef = useRef(soundOn);
  const alertsRef = useRef([]);
  const flashTimer = useRef();
  const seen = useRef(new Set());
  const firstLoad = useRef(true);

  useEffect(() => { soundRef.current = soundOn; }, [soundOn]);
  useEffect(() => { alertsRef.current = alerts; }, [alerts]);

  // Re-ring every 60s while any new order is still unacknowledged.
  useEffect(() => {
    const t = setInterval(() => {
      if (alertsRef.current.length && soundRef.current) {
        playChime();
        setFlash(true);
        window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setFlash(false), 3000);
      }
    }, 60000);
    return () => clearInterval(t);
  }, []);

  const ackOrder = (n) => setAlerts((prev) => prev.filter((a) => a.n !== n));

  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  useEffect(() => {
    api.get("/admin/takeaway/config")
      .then(({ data }) => { printerRef.current = data.printer_url || ""; setPrinterUrl(data.printer_url || ""); })
      .catch(() => {});
  }, []);

  const toggleSound = () => {
    unlockAudio();
    setSoundOn((v) => {
      const next = !v;
      localStorage.setItem("hh_kitchen_sound", next ? "on" : "off");
      if (next) playChime();
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/takeaway/orders");
      const list = data.orders || [];
      const fresh = list.filter((o) => !seen.current.has(o.id) && o.status === "new");
      if (!firstLoad.current && fresh.length) {
        if (hasPrinter(printerRef.current)) fresh.forEach((o) => printTicket(o, printerRef.current, { silent: true }));
        setAlerts((prev) => [...fresh.map((o) => ({ n: o.order_number, f: o.fulfilment, t: o.requested_time || "ASAP" })), ...prev].slice(0, 6));
        setFlash(true);
        if (soundRef.current) playChime();
        window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setFlash(false), 3000);
      }
      list.forEach((o) => seen.current.add(o.id));
      firstLoad.current = false;
      setOrders(list);
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const advance = async (o) => {
    const next = FLOW[o.status];
    if (!next) return;
    ackOrder(o.order_number);
    try {
      await api.patch(`/admin/takeaway/orders/${o.id}`, { status: next });
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

  const term = q.trim().toLowerCase();
  const match = (o) => !term || (o.name || "").toLowerCase().includes(term) || (o.phone || "").toLowerCase().includes(term);
  const filtered = orders.filter(match);
  const active = filtered.filter((o) => o.status !== "done");
  const done = filtered.filter((o) => o.status === "done");
  const alertSet = new Set(alerts.map((a) => a.n));

  const Card = ({ o }) => {
    const isAlert = alertSet.has(o.order_number);
    return (
    <div
      data-testid={`kitchen-ticket-${o.order_number}`}
      onClick={isAlert ? () => ackOrder(o.order_number) : undefined}
      className={`bg-background border-2 border-foreground rounded-2xl p-4 shadow-brutal ${isAlert ? "ring-4 ring-secondary animate-pulse cursor-pointer" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-head font-black text-2xl">#{o.order_number}</span>
        <span className={`font-head font-bold text-xs px-3 py-1 rounded-full ${BADGE[o.status]}`}>{STATUS_LABEL[o.status]}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 font-head font-bold text-sm">
        <span className="inline-flex items-center gap-1.5">{o.fulfilment === "delivery" ? <><Bike size={15} /> Delivery</> : <><ShoppingBag size={15} /> Pick up</>}</span>
        <span className="inline-flex items-center gap-1 text-secondary"><Clock size={14} /> {o.requested_time || "ASAP"}</span>
      </div>
      <ul className="mt-3 space-y-1 font-mono-plex text-sm border-t-2 border-foreground/10 pt-3">
        {(o.cart || []).map((c, i) => (
          <li key={i} className="flex justify-between"><span>{c.qty} × {c.name}</span><span>{money(c.price * c.qty)}</span></li>
        ))}
      </ul>
      <div className="flex items-center justify-between mt-2 font-mono-plex text-sm font-bold">
        <span>{money(o.total)}</span>
        <span className={o.payment_status === "paid" ? "text-secondary" : "text-foreground/50"}>{o.payment_status === "paid" ? "Paid online" : "Pay at till"}</span>
      </div>
      <div className="mt-2 font-mono-plex text-xs text-foreground/60">
        <p>{o.name} · {o.phone}</p>
        {o.address && <p>{o.address}</p>}
        {o.notes && <p className="text-foreground font-semibold mt-1">Notes: {o.notes}</p>}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={() => printTicket(o, printerRef.current)} data-testid={`kitchen-print-${o.order_number}`} className="flex items-center gap-1.5 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 hover:bg-muted"><Printer size={15} /> Print</button>
        {FLOW[o.status] && (
          <button onClick={() => advance(o)} data-testid={`kitchen-advance-${o.order_number}`} className="flex-1 font-head font-bold text-sm bg-foreground text-background rounded-full px-4 py-2 hover:opacity-90">{NEXT_LABEL[o.status]}</button>
        )}
      </div>
    </div>
    );
  };

  return (
    <div>
      {flash && <div className="fixed inset-0 pointer-events-none z-40 ring-8 ring-inset ring-secondary animate-pulse" data-testid="new-order-flash" />}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-head font-extrabold text-xl flex items-center gap-2"><ChefHat size={20} /> Takeaway orders <span className="font-mono-plex text-sm bg-primary rounded-full px-2.5 py-0.5">{active.length}</span></h2>
        <div className="flex items-center gap-2">
          <button onClick={toggleSound} data-testid="kitchen-sound-toggle" aria-pressed={soundOn} title={soundOn ? "Sound on — tap to mute" : "Sound off — tap to enable"}
            className={`flex items-center gap-1.5 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 ${soundOn ? "bg-secondary text-secondary-foreground" : "hover:bg-muted"}`}>
            {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />} Sound
          </button>
          <button onClick={load} data-testid="kitchen-refresh" className="flex items-center gap-1.5 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 hover:bg-muted"><RefreshCw size={15} /> Refresh</button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div data-testid="new-order-alert" className="flex items-center gap-3 mb-4 bg-secondary text-secondary-foreground border-2 border-foreground rounded-2xl px-4 py-3 shadow-brutal">
          <Bell size={20} className="shrink-0 animate-bounce" />
          <div className="flex-1 min-w-0">
            <p className="font-head font-extrabold">
              New order{alerts.length > 1 ? "s" : ""}!{" "}
              {alerts.map((a) => `#${a.n} (${a.f === "delivery" ? "delivery" : "pickup"}, ${a.t})`).join("  ·  ")}
            </p>
            <p className="font-mono-plex text-xs opacity-80">Chimes every minute until you tap the order or dismiss.</p>
          </div>
          <button onClick={() => setAlerts([])} data-testid="dismiss-new-order-alert" aria-label="Dismiss" className="shrink-0 p-1.5 border-2 border-current rounded-full hover:opacity-70"><X size={16} /></button>
        </div>
      )}
      <div className="relative mb-6 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="kitchen-search" placeholder="Find order by name or phone (for the till)…" className="w-full border-2 border-foreground rounded-full pl-9 pr-4 py-2.5 bg-background" />
      </div>
      <p className="text-sm text-foreground/60 mb-6">
        {hasPrinter(printerUrl)
          ? "New orders print automatically to your kitchen printer (when this screen is open inside the Star WebPRNT app on the counter iPad). Use Print on any order to reprint."
          : "New orders arrive in the queue and auto-open a print dialog for the kitchen printer while this screen is open. Set your printer IP in Takeaway settings for silent auto-printing."}
        {" "}Advance each order through the kitchen; when the customer collects, mark it collected and charge it in your till.
      </p>

      {active.length === 0 ? (
        <p className="text-foreground/50 text-sm py-10 text-center border-2 border-dashed border-foreground/20 rounded-xl">{term ? "No matching orders." : "No active orders right now."}</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{active.map((o) => <Card key={o.id} o={o} />)}</div>
      )}

      {done.length > 0 && (
        <>
          <h3 className="font-head font-bold text-foreground/50 mt-10 mb-4">Collected</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">{done.slice(0, 12).map((o) => <Card key={o.id} o={o} />)}</div>
        </>
      )}
    </div>
  );
}
